import {
  ArrowUpDownIcon,
  ChevronDownIcon,
  Columns3Icon,
  GroupIcon,
  ListFilterIcon,
  type LucideIcon,
  PlusIcon,
  Settings2Icon,
  SquarePlusIcon,
  UploadIcon,
} from "lucide-react";
import type { ComponentProps, ReactElement, ReactNode } from "react";

import { ToolbarActiveDot } from "@/components/app/workspace/toolbar-active-dot";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/*
 * The record filter bar at rest, as markup. RecordFilterBar and the controls
 * in it own views, filters, fields and columns, and render the row and their
 * closed buttons through these, so fixture records compose the same bar.
 * A `menu` wraps its button in the control's menu; without one the button
 * shows as it does at rest.
 */

type WrapInMenu = (trigger: ReactElement) => ReactNode;

const atRest: WrapInMenu = (trigger) => trigger;

/** What a menu or dialog trigger, or the bar, adds to a button: its handlers and trigger attributes. */
type TriggerProps = Omit<
  ComponentProps<typeof Button>,
  "children" | "size" | "variant"
>;

/** The filter bar's row: the view picker, a divider, the filters and view actions, then the record actions at the end. */
export function RecordFilterBarView({
  navigation,
  filters,
  actions,
}: {
  navigation?: ReactNode;
  filters?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 border-b border-border/60 bg-[var(--channel-canvas)] px-3 py-1.5">
      {navigation}
      <span aria-hidden className="mx-0.5 h-5 w-px bg-border/70" />
      {filters}
      {/* On a phone the actions can be wider than the row: they then take a
          line of their own and wrap within it, right-aligned. */}
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
        {actions}
      </div>
    </div>
  );
}

/** The view picker at rest: the open view's kind and name, or "Data" before one is chosen. */
export function ChannelViewNavigationView({
  viewName,
  icon: ViewIcon = ListFilterIcon,
  title,
  menu = atRest,
}: {
  viewName?: string | null;
  /** The open view's kind. */
  icon?: LucideIcon;
  title?: string;
  menu?: WrapInMenu;
}) {
  return (
    <div className="channel-view-navigation min-w-0">
      {menu(
        <Button
          variant="outline"
          size="sm"
          className="channel-view-navigation-trigger min-w-0 max-w-64 shrink justify-start"
          aria-label="Select view"
          title={title}
        >
          <ViewIcon className="shrink-0" />
          <span className="min-w-0 truncate">{viewName ?? "Data"}</span>
          <ChevronDownIcon
            data-icon="inline-end"
            className="ml-auto shrink-0 text-muted-foreground"
          />
        </Button>,
      )}
    </div>
  );
}

/** The view actions: one menu on a phone; from `sm`, add view and the open view's options. */
export function ChannelViewActionsView({
  selectedView,
  onAddView,
  phoneMenu = atRest,
  optionsMenu = atRest,
}: {
  /** The open saved view, which the options menu manages. */
  selectedView?: { name: string } | null;
  onAddView?: () => void;
  phoneMenu?: WrapInMenu;
  optionsMenu?: WrapInMenu;
}) {
  return (
    <>
      <div className="sm:hidden">
        {phoneMenu(
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="View actions"
            title="View actions"
          >
            <Settings2Icon />
          </Button>,
        )}
      </div>

      <div className="hidden sm:contents">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Add view"
          title="Add view"
          onClick={onAddView}
        >
          <PlusIcon />
        </Button>

        {selectedView
          ? optionsMenu(
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Manage ${selectedView.name}`}
                title="View options"
              >
                <Settings2Icon />
              </Button>,
            )
          : null}
      </div>
    </>
  );
}

/** The filters' closed "Add filter" button; the menu's trigger props pass through. */
export function RecordAddFilterTrigger(props: TriggerProps) {
  return (
    <Button variant="outline" size="sm" {...props}>
      <PlusIcon data-icon="inline-start" />
      Add filter
    </Button>
  );
}

/** The closed "Add field" button; the dialog's trigger props pass through. */
export function RecordAddFieldTrigger(props: TriggerProps) {
  return (
    <Button variant="outline" size="sm" {...props}>
      <PlusIcon data-icon="inline-start" />
      Add field
    </Button>
  );
}

/** The closed column chooser: "Columns", or how many fields show while some are hidden. */
export function RecordColumnsTrigger({
  viewName,
  visibleCount,
  fieldCount,
  className,
  ...props
}: {
  viewName: string;
  /** The view's saved visible columns. */
  visibleCount: number;
  fieldCount: number;
} & TriggerProps) {
  const someHidden = visibleCount < fieldCount;
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn("relative", className)}
      aria-label={`Choose visible columns for ${viewName}`}
      {...props}
    >
      <Columns3Icon data-icon="inline-start" />
      {someHidden ? `Columns: ${visibleCount} of ${fieldCount}` : "Columns"}
      {someHidden ? <ToolbarActiveDot /> : null}
    </Button>
  );
}

/** The closed board grouping menu, "Group: <field>", shown when a board has more than one select field; the menu's trigger props pass through. */
export function RecordGroupTrigger({
  fieldLabel,
  className,
  ...props
}: {
  /** The select field the columns come from. */
  fieldLabel: string;
} & TriggerProps) {
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn("relative min-w-0 max-w-52", className)}
      aria-label={`Group board by ${fieldLabel}`}
      {...props}
    >
      <GroupIcon data-icon="inline-start" />
      <span className="truncate">Group: {fieldLabel}</span>
      <ChevronDownIcon data-icon="inline-end" />
    </Button>
  );
}

/** The closed sort menu: "Sort", or "Sort: <field>" with a dot while a sort applies; the menu's trigger props pass through. */
export function RecordSortTrigger({
  fieldLabel = null,
  active = false,
  className,
  ...props
}: {
  /** The field the records are sorted by. */
  fieldLabel?: string | null;
  /** A sort applies. */
  active?: boolean;
} & TriggerProps) {
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn("relative min-w-0 max-w-52", className)}
      aria-label={fieldLabel !== null ? `Sort by ${fieldLabel}` : "Sort records"}
      {...props}
    >
      <ArrowUpDownIcon data-icon="inline-start" />
      <span className="truncate">
        {fieldLabel !== null ? `Sort: ${fieldLabel}` : "Sort"}
      </span>
      <ChevronDownIcon data-icon="inline-end" />
      {active ? <ToolbarActiveDot /> : null}
    </Button>
  );
}

/** Opens a new record in the data set. */
export function RecordNewRecordButton({ className, ...props }: TriggerProps) {
  return (
    <Button
      size="sm"
      className={cn("shrink-0", className)}
      aria-label="New record"
      title="New record"
      {...props}
    >
      <SquarePlusIcon data-icon="inline-start" />
      New record
    </Button>
  );
}

/** Picks PDFs or photos to add to a data set that takes documents. */
export function RecordUploadDocumentsButton({
  className,
  ...props
}: TriggerProps) {
  return (
    <Button
      size="sm"
      className={cn("shrink-0", className)}
      aria-label="Upload documents"
      title="Upload PDF or photo"
      {...props}
    >
      <UploadIcon data-icon="inline-start" />
      Upload documents
    </Button>
  );
}
